import { Component, EventEmitter, inject, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LookupService } from '../../../../services/lookup.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { UserService } from '../../../../services/user.service';
import { ListService } from '../../../../shared/services/list.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { SharedDataService } from '../../../../shared/services/shared-data-service';
import { UploadHandlerService } from '../../../../shared/services/upload-handler.service';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '@ppl/ui';
import { UploadComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { AddressComponent } from '../../../../shared/components/address/address.component';
import { PurchaseRequestService } from '../../../../services/purchase-request.service';
import { ListTableComponent } from '@ppl/ui';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { CustomValidators } from '../../../../shared/utils/custom.validator';
import { FormFieldDisplay } from '../../../../shared/interfaces/form-field-display.model';
import { LookupValueItem } from 'src/app/interfaces/lookup.interface';
import { NumberOnlyDirective } from 'src/app/shared/directives/number-only.directive';
import { TimesheetStaffErrorcodeComponent } from '../../timesheets/timesheet-staff-errorcode/timesheet-staff-errorcode.component';
import { AuditHistoryComponent } from 'src/app/shared/modals/audit-history/audit-history.component';
import { PurchaseRequestEditHistoryComponent } from '../purchase-request-edit-history/purchase-request-edit-history.component';

@Component({
  selector: 'ppl-create-purchase-request',
  standalone: true,
  imports: [
    AuditHistoryComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgZorroModule,
    ButtonComponent,
    UploadComponent,
    AddressComponent,
    NumberOnlyDirective,
    PurchaseRequestEditHistoryComponent,
    TimesheetStaffErrorcodeComponent
  ],
  templateUrl: './create-purchase-request.component.html',
  styleUrl: './create-purchase-request.component.css'
})
export class CreatePurchaseRequestComponent {
  @Input() consumerData: any = null;
  @Input() invoiceId!: any;
  @Input() mode: 'add' | 'edit' | 'view' | 'actions' = 'add';
  @Input() provider: any;
  @Input() providerId!: number;
  @Input() purchaseRequestListData?: any;
  @Input() staffView = false;
  @Input() canApproveReject = true;
  @Input() isReadOnly = false;
  @Output() purchaseRequestEvent = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<any>();
  @ViewChild('approvePurchaseRequestDialog') approvePurchaseRequestDialog!: TemplateRef<any>;
  @ViewChild('pendedDialog') pendedDialog!: TemplateRef<any>;
  @ViewChild('rejectPurchaseRequestDialog') rejectPurchaseRequestDialog!: TemplateRef<any>;
  @ViewChild(ListTableComponent) listTableChildComponent!: ListTableComponent;
  @ViewChild('purchaseRequestAuditHistoryDialog') purchaseRequestAuditHistoryDialog!: TemplateRef<any>;
  @ViewChild('purchaseRequestEditHistoryDialog') purchaseRequestEditHistoryDialog!: TemplateRef<any>;
  @ViewChild('shippingAddressComponent') shippingAddressComponent!: AddressComponent;

  private dialogSvc = inject(DialogService);

  approvePurchaseRequestDialogId: any = null;
  approvePurchaseRequestError: string | null = null;
  approvePurchaseRequestForm!: FormGroup;
  attachments: any[] = [];
  auditHistoryDialogId: any = null;
  businessStatusList: LookupValueItem[] = [];
  customErrors: string[] = [];
  duplicateErrorCodeMessage = "Duplicate PPL Purchase ID found";
  editHistoryDialogId: any = null;
  entriesProcessingStatusError: boolean = false;
  entriesProcessingStatusErrorMessage = "Approval of the Purchase Request requires all the line item entries to be in CRM Request Created processing status.";
  errorcodeData: any;
  fieldErrors: Record<number, Record<string, boolean>> = {};
  filesListLoaded: boolean = false;
  insufficientAuthMessage: string = '';
  isDisabled: boolean = false;
  isFormSubmitRequested: boolean = false;
  isParticipantSelected: boolean = false;
  isShippingAddressChecked: boolean | null = null;
  mailingValidationErrors: any;
  maxEndDate: Date = new Date();
  noServiceCodeAvailableMessage: string | null = null;
  pendedDialogId: any;
  processingStatusList: LookupValueItem[] = [];
  programName: string = '';
  purchaseRequestForm!: FormGroup;
  purchaseRequestData: any = null;
  purchaseRequestError = 'PURCHASE_REQUEST_ERROR';
  rejectPurchaseRequestDialogId: any = null;
  rejectPurchaseRequestForm!: FormGroup;
  rejectPurchaseRequestReasonList: any[] = [];
  selectedParticipantId: string = '';
  selectedPurchaseRequestLineId: any = '';
  selectedLineDetailId: any = '';
  selectedShippingAddress: string = '';
  serviceCodeList: any;
  serviceCodes: { [index: string]: string[] } = {};
  serviceDescriptionList: any = [];
  showEntries: boolean = false;
  uploadedFiles: any[] = [];
  validationErrors: any = {};
  showDeleteButton: boolean = true;
  showDownloadButton: boolean = true;
  showInsufficientAuthMessage: boolean = false;

  orderCompletedFields: FormFieldDisplay[] = [
    { label: 'Order Status', key: 'statusLkp' },
    { label: 'Order Number', key: 'orderNumber' },
    { label: 'PPL Purchase ID', key: 'pplPurchaseId' },
    { label: 'Tracking Number', key: 'trackingNumber' },
    { label: 'Shipping Amount', key: 'shippingAmount' },
    { label: 'Tax Amount', key: 'taxAmount' },
    { label: 'Total Amount', key: 'totalAmount' },
    { label: 'Remarks', key: 'remarks' }
  ];

  orderRejectedFields: FormFieldDisplay[] = [
    { label: 'Order Status', key: 'statusLkp' },
    { label: 'Rejection Reason', key: 'rejectionReasonLkp' },
    { label: 'Remarks', key: 'remarks' }
  ];

  constructor(private fb: FormBuilder,
    private sharedDataService: SharedDataService,
    private uploadHandlerService: UploadHandlerService,
    private purchaseRequestService: PurchaseRequestService,
    private userService: UserService,
    private timesheetService: TimesheetService,
    private dateFormatService: DateTimeFormatterService,
    private notificationService: NotificationService,
    private lookupService: LookupService,
    private listService: ListService) {
    this.purchaseRequestForm = this.fb.group({
      participantId: ['', [Validators.required]],
      participantPplId: ['', [Validators.required, Validators.pattern(/^(PRC-CASDP-|PA-)[a-zA-Z0-9-]*$/)]],
      participantName: [null],
      participantAddress: [''],
      isShippingAddressDifferent: [null],
      participantShippingAddress: this.fb.group({
        street1: [''],
        street2: [''],
        city: [''],
        countyOrRegion: [''],
        zipCode: [''],
        state: ['']
      }),
      vendorName: ['', [Validators.required]],
      vendorUrl: [''],
      vendorContactName: [''],
      vendorPhoneNumber: ['', [Validators.required, CustomValidators.usPhone]],
      vendorSpecialInstruction: [''],
      serviceCode: [null, [Validators.required]],
      serviceDescription: [null, [Validators.required]],
      purchaseRequestEntries: this.fb.array([])
    });

    this.approvePurchaseRequestForm = this.fb.group({
      orderNumber: [''],
      pplPurchaseId: ['', [Validators.required]],
      remarks: ['', [Validators.required]],
      itemsAmount: [{ value: '', disabled: true }],
      shippingAmount: ['', [CustomValidators.nonNegativeAmountValidator]],
      statusLkp: ['', [Validators.required]],
      tasPurchaseRequestDetailId: ['', [Validators.required]],
      taxAmount: ['', [CustomValidators.nonNegativeAmountValidator]],
      totalAmount: [{ value: '', disabled: true }, [Validators.required, CustomValidators.amountValidator]],
      trackingNumber: ['']
    });

    this.rejectPurchaseRequestForm = this.fb.group({
      tasPurchaseRequestDetailId: ['', [Validators.required]],
      statusLkp: ['', [Validators.required]],
      rejectionReasonLkp: ['', [Validators.required]],
      remarks: ['']
    });
  }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    const rejectReason = this.lookupService.getLookupValue('purchasereq_rejected_reason');
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    this.businessStatusList = this.lookupService.getLookupValue('Business Status');
    this.rejectPurchaseRequestReasonList = rejectReason.sort((a, b) => {
      if (a.lkpValueName === "Other") return 1;
      if (b.lkpValueName === "Other") return -1;
      return 0;
    });

    this.purchaseRequestEntries.valueChanges.subscribe((value) => {
      value.forEach((entry: any, index: number) => {
        const serviceUnits = parseFloat(entry.serviceUnits) || 0;
        const rate = parseFloat(entry.rate) || 0;
        const totalCost = serviceUnits * rate;
        const entryGroup = this.purchaseRequestEntries.at(index) as FormGroup;
        if (rate > 0 && serviceUnits > 0) {
          entryGroup.get('totalCost')?.setValue(totalCost.toFixed(2), { emitEvent: false });
        } else {
          entryGroup.get('totalCost')?.setValue(0, { emitEvent: false });
        }

        this.fieldErrors[index] = {
          itemDescription: entry.itemDescription ? false : true,
          serviceUnits: entry.serviceUnits ? false : true,
          rate: entry.rate > 0 ? false : true,
          totalCost: rate > 0 && serviceUnits > 0 ? false : true,
        };
      });
    });

    this.approvePurchaseRequestForm.get('shippingAmount')?.valueChanges.subscribe((value: any) => {
      this.updateTotalAmount();
    });
    this.approvePurchaseRequestForm.get('taxAmount')?.valueChanges.subscribe((value: any) => {
      this.updateTotalAmount();
    });

    if (this.mode !== 'add' && this.purchaseRequestListData) {
      this.purchaseRequestService.getPurchaseRequest(this.programName, this.purchaseRequestListData.tasPurchaserequestDetailId)
        .subscribe((data: any) => {
          this.purchaseRequestData = data.responsedata;
          const purchaseRequestLines = this.purchaseRequestData.purchaseRequestGetLine;
          this.purchaseRequestData.service = purchaseRequestLines[0].service;
          this.purchaseRequestData.serviceDescription = purchaseRequestLines[0].planServiceCode;

          purchaseRequestLines.forEach((record: any) => {
            const processingStatus = (record.prcocessingStatusLkp && this.processingStatusList?.find((status: any) =>
              status.lkpValueCode === record.prcocessingStatusLkp))?.lkpValueName ?? '';
            const businessStatus = (record.businessStatusLkp && this.businessStatusList?.find((status: any) =>
              status.lkpValueCode === record.businessStatusLkp))?.lkpValueName ?? '';
            const purchaseRequestEntry = this.fb.group({
              id: [record.tasPurchaseRequestLineDetailId],
              itemDescription: [record.itemDesc],
              serviceUrl: [record.itemUrl, [Validators.required]],
              serviceUnits: [record.unit, [Validators.required]],
              rate: [record.price, [Validators.required]],
              totalCost: [''],
              prcocessingStatus: [processingStatus],
              businessStatus: [businessStatus]
            });
            this.purchaseRequestEntries.push(purchaseRequestEntry);
          });

          this.attachments = this.purchaseRequestData?.attachmentsList.map((attachment: any) => ({
            id: attachment.fileId,
            name: attachment.fileName,
            type: 'Purchase Request Receipt'
          }));
          this.uploadedFiles = this.attachments;
          this.filesListLoaded = true;
          this.showDownloadDelete(this.purchaseRequestData.statusLkp);
          if (!this.areAllEntriesAwaitingApproval()) {
            this.entriesProcessingStatusError = true;
          }
        });
    }

    if (this.mode === 'add' && !this.staffView && this.consumerData) {
      this.purchaseRequestForm.patchValue({
        participantPplId: this.consumerData.participantPplId
      });
      this.getConsumerDetails();
    }
  }

  get purchaseRequestEntries() {
    return this.purchaseRequestForm.get('purchaseRequestEntries') as FormArray;
  }

  addEntry() {
    const purchaseRequestEntry = this.fb.group({
      selected: [false],
      id: [null],
      itemDescription: [null, [Validators.required]],
      serviceUrl: [null, [Validators.required]],
      serviceUnits: [null, [Validators.required]],
      rate: [null, [Validators.required]],
      prcocessingStatus: [null],
      businessStatus: [null],
      totalCost: [null, [Validators.required, Validators.min(0.01)]]
    });
    this.purchaseRequestEntries.push(purchaseRequestEntry);
    this.showEntries = true;
  }

  addNewEntry() {
    this.purchaseRequestForm.markAllAsTouched();
    if (this.purchaseRequestForm.get('participantPplId')?.invalid) {
      return;
    }

    this.addEntry();
  }

  areAllEntriesAwaitingApproval() {
    const allPurchaseRequestEntries = this.purchaseRequestEntries.getRawValue();
    const awaitingApprovalStatus = this.processingStatusList.find(status => status.lkpValueCode === "CRMC")?.lkpValueName;

    if (!awaitingApprovalStatus) return false;

    return allPurchaseRequestEntries.every(entry => entry.prcocessingStatus.trim().toLowerCase() === awaitingApprovalStatus.trim().toLowerCase());
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20) {
    if (searchParams.length == 0) {
      return;
    }

    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };

    return payload;
  }

  closeErrorMessage(message: string): void {
    if (message === this.entriesProcessingStatusErrorMessage) {
      this.entriesProcessingStatusError = false;
    } else if (message === this.insufficientAuthMessage) {
      this.showInsufficientAuthMessage = false;
    }
  }

  deleteRow(index: number): void {
    if (this.purchaseRequestEntries.length > 1) {
      this.purchaseRequestEntries.removeAt(index);
    }
  }

  generateLineEntries() {
    const purchaseRequestEntries = this.purchaseRequestForm.get('purchaseRequestEntries') as FormArray;

    return purchaseRequestEntries.controls
      .map(control => control.value)
      .map(entry => ({
        tasPurchaseRequestLineDetailId: entry.id,
        unit: entry.serviceUnits,
        price: entry.rate
      }));
  }

  generateSavePurchaseRequestPayload() {
    const fileId = this.uploadedFiles.length > 0 ? this.uploadedFiles.map(file => file.id) : null;
    const purchaseRequestFormValue = this.purchaseRequestForm.value;
    const payload: any = {
      participant_id: parseInt(this.selectedParticipantId),
      participant_ppl_id: purchaseRequestFormValue.participantPplId,
      consumer_name: purchaseRequestFormValue.participantName,
      shipping_address: this.selectedShippingAddress ? this.selectedShippingAddress : purchaseRequestFormValue.participantAddress,
      vendor_name: purchaseRequestFormValue.vendorName,
      vendor_url: purchaseRequestFormValue.vendorUrl,
      vendor_contact_name: purchaseRequestFormValue.vendorContactName,
      vendor_phonenumber: purchaseRequestFormValue.vendorPhoneNumber,
      special_instruction: purchaseRequestFormValue.vendorSpecialInstruction,
      status_lkp: 'SUBMITTED',
      tas_purchaserequest_fileupload_details_id: fileId,
      purchaseRequestLine: this.getPurchaseRequestEntries(purchaseRequestFormValue.serviceCode, purchaseRequestFormValue.serviceDescription)
    };

    return payload;
  }

  getConsumerDetails() {
    const participantPplId = this.purchaseRequestForm.get('participantPplId')?.value.trim();
    if (participantPplId) {
      this.purchaseRequestService.getParticipantInfo(this.programName, participantPplId)
        .subscribe((response: any) => {
          if (response.responsedata.length > 0) {
            const participantDetails = response.responsedata[0];
            this.selectedParticipantId = participantDetails.participantId;
            this.purchaseRequestForm.patchValue({
              participantId: participantDetails.participantId,
              participantName: participantDetails.participantName,
              participantAddress: participantDetails.participantAddress,
              isShippingAddressDifferent: false
            });
            this.validationErrors['participantPplId'] = null;
            this.isParticipantSelected = true;
            this.getServiceCodes();
            this.addEntry();
          } else {
            this.validationErrors['participantPplId'] = 'Participant details not found for entered PPL ID';
          }
        });
    } else {
      this.validationErrors['participantPplId'] = 'Kindly enter the Participant PPL ID';
    }
  }

  getFormGroup(controlName: string): FormGroup {
    return this.purchaseRequestForm.get(controlName) as FormGroup;
  }

  getPurchaseRequestEntries(serviceCode: any, serviceDescription: any) {
    const selectedService = this.serviceCodeList.filter((code: any) => code.serviceCodeDetailId === serviceCode);
    const selectedAuth = selectedService[0].authList.filter((auth: any) => auth.ids === serviceDescription);
    const purchaseRequestEntries: any = [];

    this.purchaseRequestEntries.controls.forEach((element: any) => {
      const entry: any = {};
      entry.service_code = selectedService[0].serviceCodeLkp;
      entry.servicecode_detail_id = parseInt(selectedService[0].serviceCodeDetailId);
      entry.auth_id = parseInt(selectedAuth[0].ids);
      entry.item_desc = element?.value?.itemDescription;
      entry.price = parseFloat(element?.value?.rate);
      entry.unit = parseInt(element?.value?.serviceUnits);
      entry.item_url = element?.value?.serviceUrl;
      entry.total = parseFloat(element?.value?.totalCost);

      purchaseRequestEntries.push(entry);
    });

    return purchaseRequestEntries;
  }

  getServiceCodes() {
    const currentDate = new Date();
    const formattedCurrentDate = this.dateFormatService.formatDateToString(currentDate);

    const payload = {
      programCode: this.programName,
      consumerId: this.selectedParticipantId,
      periodFromDate: formattedCurrentDate,
      periodToDate: formattedCurrentDate,
      source: 'purchaserequest',
      providerId: null
    };

    this.timesheetService
      .getServiceCodesByConsumer(payload)
      .subscribe((data: any) => {
        if (data.responsedata == "Auth Fail" && data.status == 404) {
          this.showEntries = false;
          this.customErrors = data.responsemsg.split(",");
          return;
        }

        if (data.responsedata?.length > 0) {
          this.noServiceCodeAvailableMessage = null;
          this.serviceCodeList = [...data.responsedata].sort((a, b) => {
            const aId = Number(a?.serviceCodeDetailId ?? 0);
            const bId = Number(b?.serviceCodeDetailId ?? 0);
            return aId - bId;
          });
        } else {
          this.noServiceCodeAvailableMessage = `There are no available service codes related to purchase request at this time.`;
        }
      });
  }

  getTotalPurchaseRequestAmount(): number {
    let total = 0;
    this.purchaseRequestEntries.controls.forEach((entry) => {
      const totalCost = entry.get('totalCost')?.value;
      if (totalCost) {
        total += parseFloat(totalCost);
      }
    });

    return total;
  }

  handleApprovePurchaseRequestClose() {
    if (this.approvePurchaseRequestDialogId) {
      this.approvePurchaseRequestForm.markAsUntouched();
      this.dialogSvc.closeDialog(this.approvePurchaseRequestDialogId);
      this.approvePurchaseRequestDialogId = null;
    }
  }

  handleApprovePurchaseRequestSubmit() {
    this.approvePurchaseRequestError = null;
    this.approvePurchaseRequestForm.enable();
    if (this.approvePurchaseRequestForm?.valid) {
      const payload = this.approvePurchaseRequestForm.value;
      payload.purchaseRequestStatusLines = this.generateLineEntries();
      this.approvePurchaseRequestForm.get('totalAmount')?.disable();

      this.purchaseRequestService.approveRejectPurchaseRequest(payload, this.programName, 'Approved', this.duplicateErrorCodeMessage)
        .subscribe((data: any) => {
          if (data?.responsemsg && Array.isArray(data.responsemsg) && data.responsemsg[0]?.code === this.purchaseRequestError && data?.status == 200) {
            if (data?.responsedata == this.duplicateErrorCodeMessage) {
              const control = this.approvePurchaseRequestForm.get('pplPurchaseId');
              control?.setErrors({ duplicate: true });
            } else {
              this.approvePurchaseRequestError = data.responsemsg[0].desc;
            }

            return;
          }

          this.handleApprovePurchaseRequestClose();
          this.purchaseRequestEvent.emit(data);
        });
    } else {
      this.approvePurchaseRequestForm.markAllAsTouched();
      this.approvePurchaseRequestForm.get('itemsAmount')?.disable();
      this.approvePurchaseRequestForm.get('totalAmount')?.disable();
    }
  }

  handleAuditHistoryDialogCancel() {
    this.selectedPurchaseRequestLineId = null;
    this.selectedLineDetailId = null;
    if (this.auditHistoryDialogId) {
      this.dialogSvc.closeDialog(this.auditHistoryDialogId);
    }
    this.auditHistoryDialogId = null;
  }

  handleEditHistoryDialogCancel() {
    this.selectedPurchaseRequestLineId = null;
    if (this.editHistoryDialogId) {
      this.dialogSvc.closeDialog(this.editHistoryDialogId);
    }
    this.editHistoryDialogId = null;
  }

  handlePendedDialogCancel() {
    if (this.pendedDialogId) {
      this.dialogSvc.closeDialog(this.pendedDialogId);
    }
    this.pendedDialogId = null;
  }

  handlePendedPurchaseRequest(status: string, id: any) {
    const filters: any = [
      {
        jsonPath: "purchaseId",
        value: id,
        operator: "Equal"
      }
    ];
    const errorListPayload = this.buildRequestPayload('purchaserequest-processCode', '', filters);
    this.listService
      .getListData(errorListPayload, 'claims')
      .subscribe((data: any) => {
        this.errorcodeData = data.responsedata?.filter((entry: any) => status === 'pended' ? entry.systemDisposition === 'PEND' : entry.systemDisposition === 'DENY');
        this.openPendedDialog(status);
      });
  }

  handleRejectPurchaseRequestClose() {
    if (this.rejectPurchaseRequestDialogId) {
      this.rejectPurchaseRequestForm.markAsUntouched();
      this.dialogSvc.closeDialog(this.rejectPurchaseRequestDialogId);
      this.rejectPurchaseRequestDialogId = null;
    }
  }

  handleRejectPurchaseRequestSubmit() {
    if (this.rejectPurchaseRequestForm?.valid) {
      const payload = this.rejectPurchaseRequestForm.value;
      payload.purchaseRequestStatusLines = this.generateLineEntries();

      this.purchaseRequestService.approveRejectPurchaseRequest(payload, this.programName, 'Rejected', this.duplicateErrorCodeMessage)
        .subscribe((data: any) => {
          this.handleRejectPurchaseRequestClose();
          this.purchaseRequestEvent.emit(data);
        });
    } else {
      this.rejectPurchaseRequestForm.markAllAsTouched();
    }
  }

  isFieldEmpty(fieldName: string, index: number): boolean {
    return this.fieldErrors[index]?.[fieldName] ?? false;
  }

  onCancel() {
    this.cancel.emit({ isCloseTriggeredByDialog: true });
  }

  onCheckUnCheck(event: any) {
    if (event) {
      this.isShippingAddressChecked = true;
    } else {
      this.isShippingAddressChecked = false;
    }
  }

  onFileSelected(event: any) {
    const file = event?.file as File;
    if (!file) return;
    const program = this.userService.getSelectedProgram()?.program_code ?? '';
    const type = this.programName || this.userService.getLeadEntry();
    const flowName = (type === 'authorizations') ? 'auth' : this.sharedDataService.getType(type);

    const formData = new FormData();
    formData.append('file', file as any);

    const requestPayload: any = {
      fileType: file.name.split('.').pop() || '',
      otherType: event.documentType || '',
      program,
      type: flowName,
      ...(flowName === 'auth' ? { authDetailId: event.leadId } : { leadId: event.leadId })
    };

    if ((event.consumerId && event.providerId) || (event.source === 'purchaserequest' && event.consumerId)) {
      Object.assign(requestPayload, {
        consumerId: event.consumerId,
        providerId: event.providerId,
        type: event.source || type
      });
    }

    formData.append('json', new Blob([JSON.stringify(requestPayload)], { type: 'application/json' }));

    this.uploadHandlerService.uploadFromEvent(event, type).subscribe({
      next: (response: any) => {
        const id = response?.responsedata?.file_upload_tracking_number ?? Date.now();
        this.attachments.push({ id, name: file.name, size: file.size, type: event.documentType });
        this.uploadedFiles = [...this.attachments];
      },
      error: () => this.notificationService.error('Error', `${file.name} file upload failed.`)
    });
  }

  onFileRemoved(fileId: any) {
    const id = fileId;
    const createType = this.purchaseRequestData ? 'detail' : 'new';
    const docSource = 'purchaserequest';
    this.uploadHandlerService.deleteFile(this.programName, id, createType, docSource).subscribe({
      next: () => {
        this.attachments = this.attachments.filter((f: any) => String(f.id) !== String(id));
        this.uploadedFiles = [...this.attachments];
      },
      error: (err) => {
        // File may have been deleted successfully but API returns error status
        // Remove from UI anyway if the file is gone
        this.attachments = this.attachments.filter((f: any) => String(f.id) !== String(id));
        this.uploadedFiles = [...this.attachments];
      }
    });
  }

  onDownloadRequested(event: any) {
    this.uploadHandlerService.downloadFile(event, 'purchaserequest');
  }

  onSave() {
    this.isFormSubmitRequested = true;
    this.purchaseRequestEntries.controls.forEach((entry, index) => {
      this.fieldErrors[index] = {};
      const itemDescription = entry.get('itemDescription')?.value;
      const serviceUrl = entry.get('serviceUrl')?.value;
      const serviceUnits = entry.get('serviceUnits')?.value;
      const rate = entry.get('rate')?.value;
      const totalCost = entry.get('totalCost')?.value;
      this.fieldErrors[index]['itemDescription'] = !itemDescription;
      this.fieldErrors[index]['serviceUrl'] = !serviceUrl;
      this.fieldErrors[index]['serviceUnits'] = !serviceUnits || serviceUnits <= 0;
      this.fieldErrors[index]['rate'] = !rate || rate <= 0;
      this.fieldErrors[index]['totalCost'] = !totalCost || totalCost <= 0;
    });

    const shippingAddress = this.purchaseRequestForm.get('participantShippingAddress')?.value;

    if (this.isShippingAddressChecked) {
      this.shippingAddressComponent.permanentValidationErrors = {};
    }

    if (this.purchaseRequestForm?.valid && this.purchaseRequestEntries.valid &&
      ((this.isShippingAddressChecked === null || this.isShippingAddressChecked === false) ||
        (this.isShippingAddressChecked && (shippingAddress.street1 !== '' && shippingAddress.city !== '' &&
          shippingAddress.state !== '' && shippingAddress.zipCode !== '' && shippingAddress.countyOrRegion !== '')))) {
      const payload = this.generateSavePurchaseRequestPayload();
      this.purchaseRequestService.savePurchaseRequest(payload, this.programName).subscribe(
        (response: any) => {
          if (response?.responsedata == "Purchase Request Auth Error" && response?.status == 200) {
            this.showInsufficientAuthMessage = true;
            this.insufficientAuthMessage = response.responsemsg[0].desc;

            return;
          }

          this.showInsufficientAuthMessage = false;
          this.insufficientAuthMessage = '';
          this.purchaseRequestEvent.emit(response.responsedata);
        });
    } else {
      if (this.isShippingAddressChecked) {
        this.shippingAddressComponent.permanentValidationErrors = {
          street1: shippingAddress.street1 ? null : 'Please enter the Address',
          city: shippingAddress.city ? null : 'Please enter the City',
          state: shippingAddress.state ? null : 'Please enter the State',
          zipCode: shippingAddress.zipCode ? null : 'Please enter the Zipcode',
          countyOrRegion: shippingAddress.countyOrRegion ? null : 'Please enter the County',
        };
      }
      this.purchaseRequestEntries.markAllAsTouched();
      this.purchaseRequestForm.markAllAsTouched();
    }
  }

  onServiceCodeSelection(event: any) {
    if (event) {
      const selectedServiceCode = this.serviceCodeList.filter((code: any) => code.serviceCodeDetailId === event);

      if (selectedServiceCode) {
        this.serviceDescriptionList = selectedServiceCode[0].authList;
      }
    } else {
      this.serviceDescriptionList = [];
    }
    this.purchaseRequestForm.get('serviceDescription')?.setValue(null, { emitEvent: false });
  }

  onShippingAddressSelected(event: any) {
    this.selectedShippingAddress = event;
    this.shippingAddressComponent.permanentValidationErrors = {};
  }

  openAuditHistoryDialog(formControlIndex: number) {
    this.selectedPurchaseRequestLineId = this.purchaseRequestListData.tasPurchaserequestDetailId;
    this.selectedLineDetailId = this.purchaseRequestEntries.at(formControlIndex).get('id')?.value;
    const dialogConfig: DialogConfig = {
      title: `Purchase Request Audit History`,
      okText: 'Ok',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'split-auth-data'
    };

    this.auditHistoryDialogId = this.dialogSvc.openDialog(
      this.purchaseRequestAuditHistoryDialog,
      dialogConfig,
      () => this.handleAuditHistoryDialogCancel(),
      () => this.handleAuditHistoryDialogCancel()
    );
  }

  openEditHistoryDialog(formControlIndex: number) {
    this.selectedPurchaseRequestLineId = this.purchaseRequestEntries.at(formControlIndex).value.id;
    const dialogConfig: DialogConfig = {
      title: `Purchase Request Edit History`,
      okText: 'Ok',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'split-auth-data'
    };

    this.editHistoryDialogId = this.dialogSvc.openDialog(
      this.purchaseRequestEditHistoryDialog,
      dialogConfig,
      () => this.handleEditHistoryDialogCancel(),
      () => this.handleEditHistoryDialogCancel()
    );
  }

  openPendedDialog(status: string) {
    const dialogConfig: DialogConfig = {
      title: `${status.charAt(0).toUpperCase() + status.slice(1)} Purchase Request`,
      okText: 'Ok',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.pendedDialogId = this.dialogSvc.openDialog(
      this.pendedDialog,
      dialogConfig,
      () => this.handlePendedDialogCancel(),
      () => this.handlePendedDialogCancel()
    );
  }

  purchaseRequestApprove() {
    this.approvePurchaseRequestError = null;
    this.isFormSubmitRequested = true;
    this.purchaseRequestEntries.controls.forEach((entry, index) => {
      this.fieldErrors[index] = {};
      const serviceUnits = entry.get('serviceUnits')?.value;
      const rate = entry.get('rate')?.value;
      this.fieldErrors[index]['serviceUnits'] = !serviceUnits || serviceUnits <= 0;
      this.fieldErrors[index]['rate'] = !rate || rate <= 0;
    });

    if (!this.purchaseRequestEntries.valid) return;

    const itemsTotalAmount = this.getTotalPurchaseRequestAmount();
    this.approvePurchaseRequestForm.patchValue({
      statusLkp: 'COMPLETED',
      itemsAmount: itemsTotalAmount,
      totalAmount: itemsTotalAmount,
      tasPurchaseRequestDetailId: this.purchaseRequestListData.tasPurchaserequestDetailId
    });

    const dialogConfig: DialogConfig = {
      title: 'Approve Purchase Request',
      okText: 'Submit',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'limit-dialog-width'
    };

    this.approvePurchaseRequestDialogId = this.dialogSvc.openDialog(
      this.approvePurchaseRequestDialog,
      dialogConfig,
      () => this.handleApprovePurchaseRequestClose(),
      () => this.handleApprovePurchaseRequestClose()
    );
  }

  purchaseRequestReject() {
    this.approvePurchaseRequestError = null;
    this.rejectPurchaseRequestForm.patchValue({
      statusLkp: 'REJECTED',
      tasPurchaseRequestDetailId: this.purchaseRequestListData.tasPurchaserequestDetailId
    });

    const dialogConfig: DialogConfig = {
      title: 'Reject Purchase Request',
      okText: 'Submit',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'limit-dialog-width'
    };

    this.rejectPurchaseRequestDialogId = this.dialogSvc.openDialog(
      this.rejectPurchaseRequestDialog,
      dialogConfig,
      () => this.handleApprovePurchaseRequestClose(),
      () => this.handleApprovePurchaseRequestClose()
    );
  }

  showDownloadDelete(status: string) {
    if (status.toLowerCase() === 'submitted' || status.toLowerCase() === 'completed' || status.toLowerCase() === 'rejected') {
      this.showDownloadButton = true;
      this.showDeleteButton = false;
    }
  }

  toggleSelectAll(event: any) {
    const isChecked = event.target.checked;
    this.purchaseRequestEntries.controls.forEach((entry: any) => {
      if (!entry.get('selected')?.disabled) {
        entry.patchValue({ selected: isChecked });
      }
    });
  }

  updateTotalAmount() {
    const shippingAmount = this.approvePurchaseRequestForm.get('shippingAmount')?.value;
    const taxAmount = this.approvePurchaseRequestForm.get('taxAmount')?.value;

    // Tax and shipping are optional; treat blank values as 0 so the grand total stays
    // correct whether or not they are entered (and updates when they are cleared).
    const itemTotalAmount = this.getTotalPurchaseRequestAmount() as any;
    const grandTotalAmount = (parseFloat(itemTotalAmount) || 0) + (parseFloat(shippingAmount) || 0) + (parseFloat(taxAmount) || 0);
    this.approvePurchaseRequestForm.patchValue({
      totalAmount: grandTotalAmount.toFixed(2)
    });
    this.approvePurchaseRequestForm.get('totalAmount')?.disable();
  }

  viewPurchaseRequestPendDetails(status: string, id: any) {
    if (['pended', 'denied'].includes(status.trim().toLowerCase())) {
      this.handlePendedPurchaseRequest(status.trim().toLowerCase(), id);
    }
  }

  onPrint(): void {
    const printSection = document.getElementById('print-section');
    if (!printSection) return;

    const styleId = 'dynamic-print-style';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    const ancestorClass = 'print-ancestor';
    let el: HTMLElement | null = printSection.parentElement;
    while (el) {
      el.classList.add(ancestorClass);
      el = el.parentElement;
    }

    styleEl.innerHTML = `
    @media print {
 
      /* Hide absolutely everything first */
      html, body {
        height: auto !important;
        overflow: visible !important;
        background: white !important;
      }
 
      body > * {
        display: none !important;
        visibility: hidden !important;
      }
 
      /* Show only the ancestor chain */
      body > .print-ancestor {
        display: block !important;
        visibility: visible !important;
      }
 
      .print-ancestor {
        display: block !important;
        visibility: visible !important;
        overflow: visible !important;
        background: white !important;
        position: static !important;
        width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        transform: none !important;
        max-width: 100% !important;
        min-height: 0 !important;
        top: auto !important;
        left: auto !important;
        right: auto !important;
        bottom: auto !important;
        z-index: auto !important;
        opacity: 1 !important;
      }
 
      /* Hide all siblings that are NOT ancestors or print-section */
      .print-ancestor > *:not(.print-ancestor):not(#print-section) {
        display: none !important;
        visibility: hidden !important;
      }
 
      /* Show print section */
      #print-section {
        display: block !important;
        visibility: visible !important;
        overflow: visible !important;
        position: static !important;
        width: 100% !important;
        height: auto !important;
        background: white !important;
        padding: 10px !important;
        margin: 0 !important;
        box-sizing: border-box !important;
        left: auto !important;
        top: auto !important;
        transform: none !important;
        z-index: auto !important;
      }
 
      #print-section * {
        visibility: visible !important;
        overflow: visible !important;
        box-sizing: border-box !important;
      }
 
      /* Hide buttons, checkboxes, actions */
      #print-section ppl-button,
      #print-section button,
      #print-section .custom-btn-row,
      #print-section .action-container,
      #print-section input[type="checkbox"],
      #print-section .ant-select-arrow,
      #print-section .ant-select-clear,
      #print-section .ant-picker-suffix,
      #print-section .ant-picker-active-bar,
      #print-section .ant-select-dropdown {
        display: none !important;
        visibility: hidden !important;
      }
 
      /* Clean up input styles */
      #print-section .ant-select-selector {
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
      }
 
      #print-section .ant-picker {
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
      }
 
      #print-section input.ant-input,
      #print-section input[nz-input] {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
      }
 
      /* Table styles */
      #print-section .table-wrapper {
        overflow: visible !important;
        width: 100% !important;
      }
 
      #print-section table {
        width: 100% !important;
        table-layout: auto !important;
        border-collapse: collapse !important;
        font-size: 11px !important;
      }
 
      #print-section th {
        background-color: #a8e4e14d !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        white-space: nowrap !important;
        padding: 5px 6px !important;
        border: 1px solid #ccc !important;
        font-size: 11px !important;
        font-weight: 700 !important;
        text-align: left !important;
      }
 
      #print-section td {
        padding: 5px 6px !important;
        border: 1px solid #ccc !important;
        font-size: 11px !important;
        vertical-align: middle !important;
        white-space: normal !important;
        word-break: break-word !important;
      }
 
      #print-section tr {
        page-break-inside: avoid !important;
      }
 
      #print-section label,
      #print-section .ppl-form-label,
      #print-section b {
        font-weight: 700 !important;
      }
 
      /* Force single page — scale content to fit */
      #print-section {
        zoom: 0.85 !important;
      }
 
      @page {
        margin: 6mm 8mm;
      }
    }
  `;

    window.print();

    window.addEventListener('afterprint', () => {
      styleEl.innerHTML = '';
      document.querySelectorAll('.' + ancestorClass).forEach(node => {
        node.classList.remove(ancestorClass);
      });
    }, { once: true });
  }
 
}
